import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/services/api';
import { supplyService } from '@/services/supplyService';
import { refreshSupplyAfterDemandPublish } from '@/modules/demand-planning/utils/refreshSupplyAfterDemandPublish';

export type ReplenishmentRefreshStepId = 'replenishment' | 'supply_cache';
export type ReplenishmentRefreshStepStatus = 'pending' | 'running' | 'done' | 'error';

export interface ReplenishmentRefreshStep {
  id: ReplenishmentRefreshStepId;
  status: ReplenishmentRefreshStepStatus;
  errorMessage?: string;
}

export type ReplenishmentRefreshModalMode = 'confirm' | 'progress';

const STEP_IDS: ReplenishmentRefreshStepId[] = ['replenishment', 'supply_cache'];

function initialSteps(): ReplenishmentRefreshStep[] {
  return STEP_IDS.map((id) => ({ id, status: 'pending' as const }));
}

function setStepStatus(
  steps: ReplenishmentRefreshStep[],
  id: ReplenishmentRefreshStepId,
  status: ReplenishmentRefreshStepStatus,
  errorMessage?: string,
): ReplenishmentRefreshStep[] {
  return steps.map((step) =>
    step.id === id ? { ...step, status, errorMessage: errorMessage ?? step.errorMessage } : step,
  );
}

function formatFlowError(
  error: unknown,
  fallback: string,
  engineNotConfiguredMessage: string,
): string {
  if (error && typeof error === 'object') {
    const apiErr = error as ApiError;
    if (apiErr.status === 503) {
      return apiErr.message || engineNotConfiguredMessage;
    }
    if (typeof apiErr.message === 'string' && apiErr.message) {
      if (apiErr.detail && apiErr.detail !== apiErr.message) {
        return `${apiErr.message}: ${apiErr.detail}`;
      }
      return apiErr.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

export function useReplenishmentRefreshFlow(options: {
  warehouseId: number | null;
  onSuccess?: () => void;
}) {
  const { warehouseId, onSuccess } = options;
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ReplenishmentRefreshModalMode>('confirm');
  const [steps, setSteps] = useState<ReplenishmentRefreshStep[]>(initialSteps);
  const [isRunning, setIsRunning] = useState(false);
  const [flowComplete, setFlowComplete] = useState(false);
  const [partialError, setPartialError] = useState<string | null>(null);
  const [replenishmentSucceeded, setReplenishmentSucceeded] = useState(false);

  const closeModal = useCallback(() => {
    if (isRunning) return;
    setModalOpen(false);
    setModalMode('confirm');
    setSteps(initialSteps());
    setFlowComplete(false);
    setPartialError(null);
    setReplenishmentSucceeded(false);
  }, [isRunning]);

  const requestRefresh = useCallback(() => {
    setModalMode('confirm');
    setSteps(initialSteps());
    setFlowComplete(false);
    setPartialError(null);
    setReplenishmentSucceeded(false);
    setModalOpen(true);
  }, []);

  const runFlow = useCallback(
    async (fromReplenishment: boolean) => {
      setIsRunning(true);
      setModalMode('progress');
      setPartialError(null);
      setFlowComplete(false);

      if (fromReplenishment) {
        setReplenishmentSucceeded(false);
      }

      let nextSteps = fromReplenishment
        ? initialSteps()
        : initialSteps().map((step) =>
            step.id === 'replenishment' ? { ...step, status: 'done' as const } : step,
          );

      if (fromReplenishment) {
        nextSteps = setStepStatus(nextSteps, 'replenishment', 'running');
        setSteps(nextSteps);

        try {
          await supplyService.recalculateOrganization({ force: true });
          nextSteps = setStepStatus(nextSteps, 'replenishment', 'done');
          setSteps(nextSteps);
          setReplenishmentSucceeded(true);
        } catch (error) {
          const message = formatFlowError(
            error,
            t('forecastReview.alerts.replenishmentRefresh.errors.replenishmentFailed'),
            t('forecastReview.alerts.replenishmentRefresh.errors.engineNotConfigured'),
          );
          nextSteps = setStepStatus(nextSteps, 'replenishment', 'error', message);
          setSteps(nextSteps);
          setPartialError(message);
          setIsRunning(false);
          return;
        }
      } else {
        setReplenishmentSucceeded(true);
      }

      nextSteps = setStepStatus(nextSteps, 'supply_cache', 'running');
      setSteps(nextSteps);

      try {
        await refreshSupplyAfterDemandPublish(warehouseId);
        nextSteps = setStepStatus(nextSteps, 'supply_cache', 'done');
        setSteps(nextSteps);
      } catch (error) {
        const message = formatFlowError(
          error,
          t('forecastReview.alerts.replenishmentRefresh.errors.supplyCacheFailed'),
          t('forecastReview.alerts.replenishmentRefresh.errors.engineNotConfigured'),
        );
        nextSteps = setStepStatus(nextSteps, 'supply_cache', 'error', message);
        setSteps(nextSteps);
        setPartialError(message);
        setIsRunning(false);
        return;
      }

      setFlowComplete(true);
      setIsRunning(false);
      onSuccess?.();
    },
    [warehouseId, onSuccess, t],
  );

  const confirmRefresh = useCallback(async () => {
    await runFlow(true);
  }, [runFlow]);

  const retryReplenishment = useCallback(async () => {
    if (!replenishmentSucceeded) {
      setPartialError(null);
      setFlowComplete(false);
      await runFlow(true);
      return;
    }
    setPartialError(null);
    setFlowComplete(false);
    const resetSteps = initialSteps().map((step) =>
      step.id === 'replenishment' ? { ...step, status: 'done' as const } : step,
    );
    setSteps(resetSteps);
    await runFlow(false);
  }, [replenishmentSucceeded, runFlow]);

  return {
    modalOpen,
    modalMode,
    steps,
    isRunning,
    flowComplete,
    partialError,
    replenishmentSucceeded,
    requestRefresh,
    confirmRefresh,
    retryReplenishment,
    closeModal,
  };
}
